import { useRef, useState } from "react";
import { Paperclip, Send, X } from "lucide-react";
import { SUPPORT_CASE_MAX_COMMENT_FILES } from "@gaole/shared";
import { api, ApiError } from "../../lib/api";
import { toast } from "../../stores/toast";
import { Button } from "../ui";

interface CaseChatComposerProps {
  disabled?: boolean;
  busy?: boolean;
  onSend: (payload: { body: string; fileKeys: string[] }) => Promise<void>;
}

export function CaseChatComposer({ disabled, busy, onSend }: CaseChatComposerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [fileKeys, setFileKeys] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const canAddMore = fileKeys.length < SUPPORT_CASE_MAX_COMMENT_FILES;

  async function uploadFiles(files: FileList | null) {
    if (!files?.length || !canAddMore) return;
    setUploading(true);
    try {
      const added: string[] = [];
      for (const file of Array.from(files)) {
        if (fileKeys.length + added.length >= SUPPORT_CASE_MAX_COMMENT_FILES) break;
        const formData = new FormData();
        formData.append("files", file);
        const res = await api<{ keys: string[] }>("/uploads/private", { formData });
        const key = res.keys[0];
        if (key) added.push(key);
      }
      if (added.length) setFileKeys((prev) => [...prev, ...added]);
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function send() {
    const body = text.trim();
    if (disabled || busy || uploading) return;
    if (!body && fileKeys.length === 0) return;
    await onSend({ body, fileKeys });
    setText("");
    setFileKeys([]);
  }

  return (
    <div className="space-y-2">
      {fileKeys.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {fileKeys.map((key) => (
            <li
              key={key}
              className="flex items-center gap-1 rounded-full border border-line bg-surface px-2 py-0.5 text-[10px] text-muted"
            >
              <span className="max-w-[8rem] truncate">{key.split("/").pop() ?? "File"}</span>
              <button
                type="button"
                className="text-muted hover:text-foreground"
                onClick={() => setFileKeys((prev) => prev.filter((k) => k !== key))}
                aria-label="Remove attachment"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-2">
        <button
          type="button"
          disabled={disabled || uploading || !canAddMore}
          onClick={() => inputRef.current?.click()}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-line text-muted hover:bg-hover disabled:opacity-40"
          aria-label="Attach files"
        >
          <Paperclip size={16} />
        </button>
        <textarea
          rows={2}
          disabled={disabled || busy}
          placeholder={disabled ? "Case is closed" : "Reply to customer…"}
          className="min-h-[2.25rem] flex-1 resize-none rounded-xl border border-line bg-input-bg px-3 py-2 text-xs text-foreground outline-none focus:border-brand-pink/50 disabled:opacity-50"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <Button
          size="sm"
          disabled={disabled || busy || uploading || (!text.trim() && fileKeys.length === 0)}
          onClick={() => void send()}
        >
          <Send size={14} />
        </Button>
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        multiple
        accept="image/*,.pdf,.doc,.docx"
        onChange={(e) => void uploadFiles(e.target.files)}
      />
    </div>
  );
}
